import { cssPropsToString } from "@/utils/style";
import type { CSSProperties } from "vue";
import type { JSX } from "vue/jsx-runtime";
import { getAttributes } from "./utils";
import { AIChart, type IAIChartData } from "../ai-chart";
import { AIDataCard, type IAIDataCardData } from "../ai-data-card";
import { CardSkeleton, ChartSkeleton } from "./skeletons";

type RichBlock =
  | { kind: "chart"; data: IAIChartData }
  | { kind: "card"; data: IAIDataCardData };

const decodeEntities = (s: string): string => {
  if (typeof document === "undefined") return s;
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};

const decodeStringsDeep = (value: unknown): unknown => {
  if (typeof value === "string") return decodeEntities(value);
  if (Array.isArray(value)) return value.map((v) => decodeStringsDeep(v));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = decodeStringsDeep(v);
    }
    return out;
  }
  return value;
};

const tryParseRichBlock = (text: string): RichBlock | null => {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
  try {
    const parsed = decodeStringsDeep(JSON.parse(trimmed)) as Record<string, unknown>;
    if (parsed && parsed.__chart === true && typeof parsed.type === "string" && Array.isArray(parsed.labels) && Array.isArray(parsed.datasets)) {
      return { kind: "chart", data: parsed as unknown as IAIChartData };
    }
    if (parsed && parsed.__card === true && typeof parsed.title === "string" && Array.isArray(parsed.fields)) {
      return { kind: "card", data: parsed as unknown as IAIDataCardData };
    }
  } catch {
    return null;
  }
  return null;
};

const parseHTML = (content: string): Element | Node | HTMLElement | Text | Comment | HTMLCollection | NodeList | null => {
  try {
  const root = document.createElement('div');
  const style = cssPropsToString({
    display: 'contents'
  });
  root.setAttribute('style', style);
  root.innerHTML = content

 if (root.children.length === 1 && root.childNodes.length === 1) {
   return Array.from(root.children)[0];
 } else if (root.childNodes.length === 1) {
   return Array.from(root.childNodes)[0];
 } else if (root.childNodes.length > 0) {
   return root.childNodes;
 } else if (root.children.length > 0) {
   return root.children;
 }

    return root;
  } catch (e) {
    console.error(e);
    return null;
  }
}



type Rendered = string | JSX.Element | undefined | null;

const childrenOrText = (el: Element | Node): HTMLCollection | NodeList | string => {
  if (el.childNodes.length > 0) return el.childNodes;

  if (el instanceof Element) {
    if (el.children.length > 0) return el.children;
  }
  
  return el.textContent || '';
};

export type RenderOptions = {
  inline?: boolean;
};

type RenderContext = {
  depth: number;
  insideLi: boolean;
  insidePre: boolean;
};

const normalizeLanguage = (raw: string): string => {
  const cleaned = raw.replace(/^language-/, "").trim().toLowerCase();
  if (!cleaned) return "";
  const aliases: Record<string, string> = {
    js: "javascript",
    ts: "typescript",
    py: "python",
    sh: "bash",
    yml: "yaml",
    md: "markdown",
  };
  return aliases[cleaned] || cleaned;
};

const CodeBlock = defineComponent<{ language?: string; source?: string }>(
  (props, ctx) => {
    const copied = ref(false);
    let resetTimer: ReturnType<typeof setTimeout> | null = null;

    const handleCopy = async () => {
      const text = props.source ?? "";
      if (!text || typeof navigator === "undefined" || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(text);
        copied.value = true;
        if (resetTimer) clearTimeout(resetTimer);
        resetTimer = setTimeout(() => {
          copied.value = false;
        }, 1400);
      } catch {
        // clipboard denied; leave state alone
      }
    };

    return () => {
      const lang = normalizeLanguage(props.language || "");
      const canCopy =
        typeof props.source === "string" && props.source.trim().length > 0;

      return (
        <div class="group relative my-3 w-full overflow-hidden rounded-xl bg-[#0f172a] ring-1 ring-inset ring-white/5 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_-16px_rgba(15,23,42,0.4)]">
          <div class="flex items-center justify-between gap-3 border-b border-white/5 bg-white/[0.02] px-3.5 py-1.5">
            <span class="select-none font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
              {lang || "code"}
            </span>
            {canCopy ? (
              <button
                type="button"
                onClick={handleCopy}
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 opacity-0 transition-all hover:text-slate-100 group-hover:opacity-100 focus:opacity-100"
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  {copied.value ? (
                    <polyline points="20 6 9 17 4 12" />
                  ) : (
                    <>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </>
                  )}
                </svg>
                <span>{copied.value ? "Copied" : "Copy"}</span>
              </button>
            ) : null}
          </div>
          <pre class="pretty-scrollbar dark m-0 overflow-x-auto px-4 py-3 text-[13px] leading-relaxed text-slate-100">
            <code class="block font-mono whitespace-pre [font-feature-settings:'calt','liga','ss01']">
              {ctx.slots.default ? ctx.slots.default() : ""}
            </code>
          </pre>
        </div>
      );
    };
  },
  { props: ["language", "source"] },
);

const renderNode = (
  el: Element | NodeList | Node | string | Text | Comment | HTMLCollection,
  options: RenderOptions,
  ctx: RenderContext = { depth: 0, insideLi: false, insidePre: false },
): Rendered | Array<Rendered> => {
  if (typeof el === "string") return el;
  if (el instanceof Text) return el.textContent || el.data;
  if (el instanceof Comment) return el.textContent || el.data;
  if (el instanceof HTMLCollection) {
    return Array.from(el)
      .map((it) => renderNode(it, options, ctx))
      .flat();
  }
  if (el instanceof NodeList) {
    return Array.from(el)
      .map((it) => renderNode(it, options, ctx))
      .flat();
  }



  const attribs = getAttributes(el);
  const name = el instanceof Element ? el.tagName.toLowerCase() : el.nodeName.toLowerCase();

  const maybeInlineStyle: CSSProperties = {
    ...(options.inline
      ? {
          display: "inline-block",
          marginRight: "0.5ch",
          maxWidth: "200px",
        }
      : {}),
  };

  // Pure numeric/currency/percentage content inside <strong> gets a subtle
  // data-chip treatment so figures stand out from prose. Matches things like
  // "~92.7%", "3.6M EUR", "720,000 EUR", "20%", "$1.2B", "≈ 1.8M EUR".
  const NUMERIC_STRONG_RE =
    /^[\s~≈]*[-+]?\$?[\d][\d.,\s]*\s*(?:[KkMmBbTt]|%)?(?:\s+[A-Z]{3})?\s*$/;
  const isNumericEmphasis = (node: Element | Node): boolean => {
    const raw = (node.textContent || "").trim();
    if (raw.length === 0 || raw.length > 24) return false;
    if (!/\d/.test(raw)) return false;
    return NUMERIC_STRONG_RE.test(raw);
  };

  const map: Record<string, () => JSX.Element> = {
    div: () => {
      const skeletonKind =
        el instanceof Element ? el.getAttribute("data-ai-skeleton") : null;
      if (skeletonKind === "chart") return <ChartSkeleton />;
      if (skeletonKind === "card") return <CardSkeleton />;
      return (
        <div class="w-full space-y-3">{renderNode(childrenOrText(el), options, ctx)}</div>
      );
    },
    span: () => <span class="space-y-3">{renderNode(childrenOrText(el), options, ctx)}</span>,
    p: () => (
      <p
        class={[
          "text-gray-800 text-[14px] leading-[1.65] font-normal font-['Inter'] [font-feature-settings:'cv11','ss01'] [text-wrap:pretty]",
          ctx.insideLi ? "inline" : "",
        ]}
        style={maybeInlineStyle}
      >
        {renderNode(childrenOrText(el), options, ctx)}
      </p>
    ),
    h1: () => (
      <h1
        class={[
          "text-gray-900 text-[20px] font-semibold leading-[1.25] tracking-[-0.018em] font-['Inter'] [text-wrap:balance] first:mt-0 mt-5",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </h1>
    ),
    h2: () => (
      <h2
        class={[
          "text-gray-900 text-[17px] font-semibold leading-[1.3] tracking-[-0.015em] font-['Inter'] [text-wrap:balance] first:mt-0 first:border-t-0 first:pt-0 mt-4 pt-3 border-t border-gray-200/70",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </h2>
    ),
    h3: () => (
      <h3
        class={[
          "text-gray-900 text-[15.5px] font-semibold leading-[1.35] tracking-[-0.011em] font-['Inter'] [text-wrap:balance] first:mt-0 first:border-t-0 first:pt-0 mt-4 pt-3 border-t border-gray-200/70",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </h3>
    ),
    h4: () => (
      <h4
        class={[
          "text-gray-900 text-[13px] font-semibold leading-snug tracking-[0.04em] uppercase font-['Inter'] first:mt-0 mt-4",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </h4>
    ),
    h5: () => (
      <h5
        class={[
          "text-gray-700 text-[12px] font-semibold leading-snug tracking-[0.06em] uppercase font-['Inter'] first:mt-0 mt-3",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </h5>
    ),
    hr: () => (
      <div class="w-full grid place-items-center my-5" aria-hidden="true">
        <div class="flex items-center gap-1.5 text-gray-300">
          <span class="h-px w-10 bg-gradient-to-r from-transparent to-gray-300" />
          <span class="size-[3px] rounded-full bg-gray-300" />
          <span class="size-[3px] rounded-full bg-gray-300" />
          <span class="size-[3px] rounded-full bg-gray-300" />
          <span class="h-px w-10 bg-gradient-to-l from-transparent to-gray-300" />
        </div>
      </div>
    ),
    i: () => (
      <i
        class={[
          "text-gray-600 italic font-normal font-['Inter']",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </i>
    ),
    em: () => (
      <em
        class={[
          "text-gray-600 italic font-normal font-['Inter']",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </em>
    ),
    strong: () => {
      if (isNumericEmphasis(el)) {
        return (
          <strong
            class={[
              "inline rounded-md bg-primary-50/70 px-1.5 py-[1px] align-baseline font-['Inter'] text-[0.92em] font-semibold tracking-[-0.005em] text-primary-800 ring-1 ring-inset ring-primary-100 [font-variant-numeric:tabular-nums]",
            ]}
          >
            {renderNode(childrenOrText(el), options, {
              ...ctx,
              depth: ctx.depth + 1,
            })}
          </strong>
        );
      }
      return (
        <strong
          class={[
            "text-gray-900 font-semibold tracking-[-0.005em] font-['Inter'] [font-feature-settings:'tnum','cv11','ss01']",
          ]}
        >
          {renderNode(childrenOrText(el), options, {
            ...ctx,
            depth: ctx.depth + 1,
          })}
        </strong>
      );
    },
    b: () => {
      if (isNumericEmphasis(el)) {
        return (
          <b
            class={[
              "inline rounded-md bg-primary-50/70 px-1.5 py-[1px] align-baseline font-['Inter'] text-[0.92em] font-semibold tracking-[-0.005em] text-primary-800 ring-1 ring-inset ring-primary-100 [font-variant-numeric:tabular-nums]",
            ]}
          >
            {renderNode(childrenOrText(el), options, {
              ...ctx,
              depth: ctx.depth + 1,
            })}
          </b>
        );
      }
      return (
        <b
          class={[
            "text-gray-900 font-semibold tracking-[-0.005em] font-['Inter'] [font-feature-settings:'tnum','cv11','ss01']",
          ]}
        >
          {renderNode(childrenOrText(el), options, {
            ...ctx,
            depth: ctx.depth + 1,
          })}
        </b>
      );
    },
    br: () => <br />,
    a: () => (
      <a {...attribs} class="text-primary-400">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </a>
    ),
    section: () => (
      <section class="w-full">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </section>
    ),
    ul: () => (
      <ul
        class={[
          "space-y-1.5 list-disc list-outside pl-5 text-[14px] leading-[1.65] marker:text-primary-400",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </ul>
    ),
    ol: () => (
      <ol
        class={[
          "space-y-1.5 list-decimal list-outside pl-6 text-[14px] leading-[1.65] marker:text-primary-600 marker:font-semibold [font-variant-numeric:tabular-nums]",
        ]}
      >
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </ol>
    ),
    li: () => (
      <li class="text-gray-800 pl-1.5 leading-[1.65] [&>p]:my-0">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
          insideLi: true,
        })}
      </li>
    ),
    pre: () => {
      // Rich blocks (charts, data cards) arrive as <pre><code class="chart">{JSON}</code></pre>.
      // Detect at the <pre> level so the match short-circuits the whole code-block
      // chrome — otherwise the AIChart would render inside the gray code card.
      const codeEl =
        el instanceof Element ? el.querySelector("code") : null;
      const rawText = (codeEl?.textContent || "").trim();
      const rich = rawText.length > 0 ? tryParseRichBlock(rawText) : null;
      if (rich) {
        if (rich.kind === "chart") return <AIChart data={rich.data} />;
        return <AIDataCard data={rich.data} />;
      }
      if (rawText.includes('"__chart":true')) return <ChartSkeleton />;
      if (rawText.includes('"__card":true')) return <CardSkeleton />;

      const language = codeEl?.getAttribute("class") || "";
      const source = codeEl?.textContent || "";
      return (
        <CodeBlock language={language} source={source}>
          {renderNode(childrenOrText(el), options, {
            ...ctx,
            depth: ctx.depth + 1,
            insidePre: true,
          })}
        </CodeBlock>
      );
    },
    code: () => {
      // When nested inside <pre>, the block chrome handles layout — render
      // just the text so CodeBlock's own <pre><code> wrapper stays in charge
      // of whitespace and overflow.
      if (ctx.insidePre) {
        return (
          <>
            {renderNode(childrenOrText(el), options, {
              ...ctx,
              depth: ctx.depth + 1,
            })}
          </>
        );
      }

      // Safety: if showdown emitted a <code> (without <pre>) around rich-block
      // JSON during streaming, don't leak raw payload into the text — show
      // the matching skeleton instead until the real block lands.
      const rawText = (el.textContent || "").trim();
      const rich = rawText.length > 0 ? tryParseRichBlock(rawText) : null;
      if (rich) {
        if (rich.kind === "chart") return <AIChart data={rich.data} />;
        return <AIDataCard data={rich.data} />;
      }
      if (rawText.includes('"__chart":true')) return <ChartSkeleton />;
      if (rawText.includes('"__card":true')) return <CardSkeleton />;

      // Standalone <code> = markdown inline code (`foo`) — render a compact
      // typographic pill that sits inside running text instead of the old
      // dark card.
      return (
        <code class="inline rounded-[4px] bg-gray-100 px-2 py-[3px] align-baseline font-mono text-[0.88em] leading-[1.45] text-gray-800 ring-1 ring-inset ring-gray-900/5 [font-feature-settings:'calt','liga']">
          {renderNode(childrenOrText(el), options, {
            ...ctx,
            depth: ctx.depth + 1,
          })}
        </code>
      );
    },
    table: () => (
      <div class="w-full overflow-x-auto my-4 rounded-lg ring-1 ring-gray-200/80">
        <table class="w-full border-collapse text-[13.5px] font-['Inter'] [font-variant-numeric:tabular-nums]">
          {renderNode(childrenOrText(el), options, {
            ...ctx,
            depth: ctx.depth + 1,
          })}
        </table>
      </div>
    ),
    thead: () => (
      <thead class="bg-gray-50/80 border-b border-gray-200">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </thead>
    ),
    tbody: () => (
      <tbody>
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </tbody>
    ),
    tr: () => (
      <tr class="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </tr>
    ),
    th: () => (
      <th class="text-left px-3 py-2 text-gray-700 text-[11px] font-semibold uppercase tracking-[0.06em] font-['Inter']">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </th>
    ),
    td: () => (
      <td class="px-3 py-2 text-gray-800 font-normal font-['Inter'] align-top">
        {renderNode(childrenOrText(el), options, {
          ...ctx,
          depth: ctx.depth + 1,
        })}
      </td>
    ),
  };

  const fn = map[name];

  if (fn) return fn();

  if (el instanceof Element) {
    return <div v-html={el.innerHTML} class="w-full space-y-2"></div>;
  }

  return <div v-html={el.textContent || ''} class="w-full"></div>;
};

export const renderMessage = (
  text: string,
  options: RenderOptions = {},
): JSX.Element => {
  const el = parseHTML(text);
  if (!el) return <span>{text}</span>;

  return <>{renderNode(el, options)}</>;
};
